import Styles from './CreditUsageDropdown.module.scss'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { Button } from 'blaze-ui/Button'
import Server02 from 'blaze-ui/icons/16/Server02'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Text } from 'blaze-ui/Text/Text'
import { HelpCircleContained } from 'blaze-ui/icons/24'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useModals } from 'almanac-ui/Modal'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import dayjs from 'dayjs'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'

export const CreditUsageDropdown = () => {
  const isPioneerCreditsEnabled = useFlipperFlag('pioneer_credits')
  const workspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const {
    tierUsageStore: { currentSubscription, autopilotAddOns },
    creditUsageStore: { creditUsage },
  } = useRootStore()
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const isTrialing = currentSubscription?.status === 'trialing'
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isOnPioneerTrial = isTrialing && isOnPioneer
  const brandKitAndStockContentCreditsUsed =
    (creditUsage?.content_credits_used ?? 0) + (creditUsage?.ads_credits_used ?? 0)
  const refillDate = dayjs(creditUsage?.end_date).add(1, 'day')

  if (
    !workspace?.is_virtual_marketer ||
    workspace.is_ap_freemium ||
    isOnPioneerTrial ||
    !currentSubscription ||
    !isPioneerCreditsEnabled
  ) {
    return null
  }

  const openAutopilotPlansModal = () => {
    openModal(AutopilotPlansModal)
  }

  const openCreditsModal = () => {
    if (!autopilotContentCreditsPrice) return

    openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice })
  }

  return (
    <DropdownTrigger placement="bottom-end">
      <Button rounded frontIcon={Server02} variant="tertiary" className={Styles.button}>
        {creditUsage?.credits_remaining === 0 ? 'Add Credits' : `${creditUsage?.credits_remaining}`}
      </Button>
      {(close) => {
        return (
          <div className={Styles.dropdownContent}>
            <div className={Styles.creditsLeftContainer}>
              <div className={Styles.item}>
                <Text variant="smallList" color="var(--dark-80)">
                  Credits Left
                </Text>
                <Text variant="smallList" color="var(--dark-80)">
                  {creditUsage?.credits_remaining}
                </Text>
              </div>
              {!isOnPioneer && !isTrialing && (
                <div className={Styles.item}>
                  <div className={Styles.refillInfo}>
                    <Text variant="metadata" color="var(--dark-40)">
                      Credits added on {refillDate.format('MMM D')} as part of {nameMap[currentSubscription.plan]} Plan
                    </Text>
                  </div>
                  <Text variant="metadata" color="var(--dark-40)">
                    +{creditUsage?.initial_credits_available}
                  </Text>
                </div>
              )}
            </div>

            <div className={Styles.usageBreakdownContainer}>
              <Tooltip
                placement="bottom"
                label={
                  <div className={Styles.tooltip}>
                    <Text variant="smallList" color="var(--light-100)" lineClamp={1}>
                      Credit cost per generation
                    </Text>
                    <div className={Styles.cost}>
                      <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                        Animated Product Video
                      </Text>
                      <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                        {creditUsage?.usage_costs.video_credits_cost}
                      </Text>
                    </div>
                    <div className={Styles.cost}>
                      <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                        Stylized Image
                      </Text>
                      <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                        {creditUsage?.usage_costs.visual_styles_credits_cost}
                      </Text>
                    </div>
                    {!isOnPioneer && (
                      <div className={Styles.cost}>
                        <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                          Brand Kit and Stock Content
                        </Text>
                        <Text variant="metadata" color="var(--light-100)" lineClamp={1}>
                          {creditUsage?.usage_costs.content_credits_cost}
                        </Text>
                      </div>
                    )}
                  </div>
                }
                hide={false}
              >
                <div className={Styles.header}>
                  <Text variant="smallList" color="var(--dark-80)">
                    Usage Breakdown
                  </Text>
                  <HelpCircleContained size={12} color="var(--dark-40)" />
                </div>
              </Tooltip>
              <div className={Styles.breakdown}>
                <Text variant="metadata" color="var(--dark-40)">
                  Animated Product Video
                </Text>
                <Text variant="metadata" color="var(--dark-40)">
                  {creditUsage?.video_credits_used}
                </Text>
              </div>
              <div className={Styles.breakdown}>
                <Text variant="metadata" color="var(--dark-40)">
                  Stylized Image
                </Text>
                <Text variant="metadata" color="var(--dark-40)">
                  {creditUsage?.visual_styles_credits_used}
                </Text>
              </div>
              {!isOnPioneer && (
                <div className={Styles.breakdown}>
                  <Text variant="metadata" color="var(--dark-40)">
                    Brand Kit and Stock Content
                  </Text>
                  <Text variant="metadata" color="var(--dark-40)">
                    {brandKitAndStockContentCreditsUsed}
                  </Text>
                </div>
              )}
            </div>
            <div className={Styles.buttonContainer}>
              <Button
                onPress={() => {
                  close()
                  if (isTrialing) {
                    openAutopilotPlansModal()
                  } else {
                    openCreditsModal()
                  }
                }}
                variant="ghost-tertiary"
                fullWidth
              >
                {isTrialing ? 'Upgrade' : 'Add More Credits'}
              </Button>
            </div>
          </div>
        )
      }}
    </DropdownTrigger>
  )
}
